import React, { useEffect, useState, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../contexts/OnboardingContext';
import { lessonsService } from '../services/lessonsService';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { useObjectives } from '../../goal/contexts/ObjectiveContext';
import { useAnchorTooltip } from '../hooks/useAnchorTooltip';
import { LessonTooltip } from './LessonTooltip';
import { LessonGuideShell } from './LessonGuideShell';

type Step = 'create-goal' | 'pick-goal' | 'suggest' | 'add-task' | 'done';

interface Lesson3GuideProps {
  onClose: () => void;
}

const HEADER_LABEL: Record<Step, string> = {
  'create-goal': 'Step 1',
  'pick-goal': 'Step 1',
  'suggest': 'Step 2',
  'add-task': 'Step 3',
  'done': 'Done',
};

const STEP_TITLES: Record<Step, string> = {
  'create-goal': 'First, create a goal',
  'pick-goal': 'Pick a goal',
  'suggest': 'Ask for suggestions',
  'add-task': 'Add a task',
  'done': 'Your first step is ready',
};

/**
 * Lesson3Guide
 *
 * Onboarding Lesson 3 的引導卡片（右下角浮動）。
 * 掛載於 AppLayout，跨 GoalsPage / GoalDetailPage 持續存在。
 *
 * 步驟流程：
 * - create-goal：沒有 active goals，引導建立 Goal（tooltip 指向 New Goal 按鈕）
 * - pick-goal：有 goals，引導點進一個 Goal（tooltip 指向第一張 Goal 卡片）
 * - suggest：進入 Goal Detail，tooltip 指向 Suggest 按鈕
 * - add-task：tooltip 指向第一個建議 Task 的 + 按鈕
 * - done：Task 建立成功，顯示完成訊息
 */
export function Lesson3Guide({ onClose }: Lesson3GuideProps) {
  const { setActiveLessonGuide } = useOnboarding();
  const { activeObjectives } = useObjectives();
  const navigate = useNavigate();
  const hasActiveGoals = activeObjectives.length > 0;

  // 起始 step：有 goals 直接進 pick-goal
  const [step, setStep] = useState<Step>(hasActiveGoals ? 'pick-goal' : 'create-goal');

  // 若用戶在引導進行中刪除了所有 goal，退回 create-goal
  useEffect(() => {
    if (!hasActiveGoals) {
      setStep((prev) => (prev === 'pick-goal' ? 'create-goal' : prev));
    }
  }, [hasActiveGoals]);

  // 建立 Modal 是否開著（開著時隱藏 New Goal tooltip）
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    setActiveLessonGuide('lesson-3');
    return () => setActiveLessonGuide(null);
  }, [setActiveLessonGuide]);

  // --- EventBus subscriptions ---

  // Modal 開啟 → 隱藏 New Goal tooltip
  useEffect(() => {
    return eventBus.subscribe(EVENTS.GOAL.CREATE_MODAL_OPENED, () => {
      setCreateModalOpen(true);
    });
  }, []);

  // Modal 取消關閉 → 重新顯示 tooltip
  useEffect(() => {
    return eventBus.subscribe(EVENTS.GOAL.CREATE_MODAL_CLOSED, () => {
      setCreateModalOpen(false);
    });
  }, []);

  // 建立 Goal 成功 → 推進到 pick-goal
  useEffect(() => {
    return eventBus.subscribe(EVENTS.GOAL.CREATED, () => {
      setCreateModalOpen(false);
      setStep((prev) => (prev === 'create-goal' ? 'pick-goal' : prev));
    });
  }, []);

  // 點擊 Goal 卡片 → 推進到 suggest
  useEffect(() => {
    return eventBus.subscribe(EVENTS.UI.GOAL_ACTIONS_OPENED, () => {
      setStep((prev) => (prev === 'pick-goal' ? 'suggest' : prev));
    });
  }, []);

  // 點擊 Suggest → 推進到 add-task step
  useEffect(() => {
    return eventBus.subscribe(EVENTS.UI.GOAL_SUGGEST_CLICKED, () => {
      setStep((prev) => (prev === 'suggest' ? 'add-task' : prev));
    });
  }, []);

  // GoalBreakdownPanel 建立 Task → 完成
  useEffect(() => {
    return eventBus.subscribe(EVENTS.LESSON.GOAL_TASK_CREATED, () => {
      lessonsService.markCompleted('lesson-3');
      setStep('done');
    });
  }, []);

  // --- Tooltip positioning (useAnchorTooltip) ---

  const calcCenterBelow = useCallback(
    (rect: DOMRect): React.CSSProperties => ({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
      transform: 'translateX(-50%)',
    }),
    [],
  );

  const newGoalStyle = useAnchorTooltip({
    anchor: 'goal-new-goal',
    enabled: step === 'create-goal' && !createModalOpen,
    calcStyle: calcCenterBelow,
  });

  const goalCardStyle = useAnchorTooltip({
    anchor: 'goal-card',
    enabled: step === 'pick-goal',
    calcStyle: calcCenterBelow,
  });

  const suggestStyle = useAnchorTooltip({
    anchor: 'goal-suggest',
    enabled: step === 'suggest',
    scrollIntoView: true,
    calcStyle: useCallback(
      (rect: DOMRect): React.CSSProperties => ({
        position: 'fixed',
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      }),
      [],
    ),
  });

  const addTaskStyle = useAnchorTooltip({
    anchor: 'goal-add-task',
    enabled: step === 'add-task',
    calcStyle: useCallback(
      (rect: DOMRect): React.CSSProperties => ({
        position: 'fixed',
        top: rect.bottom + 12,
        left: rect.left - 8,
      }),
      [],
    ),
  });

  // --- Render ---

  return (
    <>
      <LessonGuideShell
        lessonId="lesson-3"
        ariaLabel="Lesson 3: Move a goal forward"
        subtitle={`Move a goal forward · ${HEADER_LABEL[step]}`}
        title={STEP_TITLES[step]}
        onClose={onClose}
      >
        {step === 'create-goal' && (
          <p className="text-sm text-gray-500 leading-relaxed">
            Create a goal to get started — tap{' '}
            <span className="font-medium text-gray-700">New Goal</span> or the{' '}
            <span className="inline-flex items-center justify-center w-5 h-5 bg-sky-600 text-white text-xs rounded font-medium">+</span>{' '}
            button.
          </p>
        )}

        {step === 'pick-goal' && (
          <p className="text-sm text-gray-500 leading-relaxed">
            Tap any goal to open it and start breaking it down.
          </p>
        )}

        {step === 'suggest' && (
          <p className="text-sm text-gray-500 leading-relaxed">
            Tap <span className="font-medium text-teal-600">Suggest</span> — Orlo will suggest steps to move this goal forward.
          </p>
        )}

        {step === 'add-task' && (
          <p className="text-sm text-gray-500 leading-relaxed">
            Tap the <span className="inline-flex items-center justify-center w-4 h-4 bg-teal-500 text-white text-xs rounded-full font-bold">+</span>{' '}
            next to a suggestion to add it as a task.
          </p>
        )}

        {step === 'done' && (
          <>
            <p className="text-sm text-gray-500 leading-relaxed">
              You've broken a goal into an actionable task. Keep going — one step at a time.
            </p>
            <button
              onClick={() => navigate('/today')}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-sky-600 text-white text-sm font-medium py-2 hover:bg-sky-700 transition-colors"
            >
              Back to today
              <ArrowRight size={14} />
            </button>
          </>
        )}
      </LessonGuideShell>

      {newGoalStyle && <LessonTooltip style={newGoalStyle}>Tap here to create your first goal</LessonTooltip>}
      {goalCardStyle && <LessonTooltip style={goalCardStyle}>Tap on a goal to open it</LessonTooltip>}
      {suggestStyle && <LessonTooltip style={suggestStyle} arrowAlign="right">Tap to get step suggestions</LessonTooltip>}
      {addTaskStyle && <LessonTooltip style={addTaskStyle} arrowAlign="left">Tap + to add this step</LessonTooltip>}
    </>
  );
}
